import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, XCircle, Search } from 'lucide-react';
import apiClient, {
  acknowledgementService,
  approvalService,
  vendorService,
  getErrorMessage,
} from '../../services/apiService';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import {
  supplierTermsOfPayment,
  supplierPaymentMethods,
  supplierDeliveryTerms,
  supplierModeOfDelivery,
} from '../../utils/questionnaireOptions';
import { getQuestionnaireLabel } from '../../utils/questionnaireOptions';
import { useToast } from '../../components/ui/Toast';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useConfirm } from '../../hooks/useConfirm';

const REJECTION_REASONS = [
  { value: 'incomplete_documents', label: 'Incomplete Documents' },
  { value: 'invalid_information', label: 'Invalid Information' },
  { value: 'compliance_issues', label: 'Compliance Issues' },
  { value: 'duplicate_vendor', label: 'Duplicate Vendor' },
  { value: 'other', label: 'Other (specify)' },
];

const ConflictingVendors = () => {
  // useToast() returns { toasts, toast, success, error, warning, info, ... } where
  // `toast` is a bare function with no severity methods. Destructuring it made
  // every toast.error()/toast.success() call throw, which also aborted the list
  // refresh that followed it. Take the whole context.
  const toast = useToast();
  const { confirmState, confirm, setConfirmState } = useConfirm();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({});
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});

  // Rejection modal state
  const [rejectionVendor, setRejectionVendor] = useState(null);
  const [showRejectionOptions, setShowRejectionOptions] = useState(false);
  const [showRejectionForm, setShowRejectionForm] = useState(false);
  const [showPermanentRejectionConfirmation, setShowPermanentRejectionConfirmation] = useState(false);
  const [rejectionType, setRejectionType] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [rejectionRemarks, setRejectionRemarks] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);

  // Initialize selected values when vendor is selected
  useEffect(() => {
    if (selectedVendor) {
      const vendor = vendors.find(v => v.id === selectedVendor);
      if (vendor) {
        const vendorData = vendor.vendor_response || {};
        setSelectedValues(vendorData);
      }
    } else {
      setSelectedValues({});
    }
  }, [selectedVendor, vendors]);

  useEffect(() => {
    fetchConflictedVendors();
  }, [page, search]);

  const fetchConflictedVendors = async () => {
    try {
      setLoading(true);
      const response = await acknowledgementService.getConflicted(page, 25, search);
      
      // Safely handle response and normalize data structure for old data
      const acknowledgements = (response.data || []).map(ack => ({
        ...ack,
        questionnaire_data: (ack.questionnaire_data && typeof ack.questionnaire_data === 'object')
          ? ack.questionnaire_data
          : {},
        vendor_response: (ack.vendor_response && typeof ack.vendor_response === 'object')
          ? ack.vendor_response
          : {},
      }));
      
      setVendors(acknowledgements);
      setMeta(response.meta || {});
    } catch (err) {
      console.error('Failed to fetch conflicted vendors:', err);
      setVendors([]);
      setMeta({});
      // Don't show toast on initial load if backend isn't ready
      if (err.response?.status !== 404 && err.response?.status !== 500 && err.response?.status !== 304) {
        toast.error('Failed to fetch conflicted vendors: ' + (err.response?.data?.message || err.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (acknowledgementId) => {
    // Validate that all required fields are selected
    const requiredFields = [
      'supplier_term_of_payment',
      'supplier_payment_method',
      'supplier_delivery_terms',
      'supplier_mode_of_delivery'
    ];
    
    const missingFields = requiredFields.filter(field => !selectedValues[field]);
    
    if (missingFields.length > 0) {
      toast.warning(`Please select values for all fields. Missing: ${missingFields.map(f => f.replace(/_/g, ' ')).join(', ')}`);
      return;
    }

    const confirmed = await confirm({
      title: 'Approve Changes',
      message: 'Send these final values to vendor for their approval?',
      variant: 'warning'
    });

    if (!confirmed) return;

    try {
      await acknowledgementService.resolve(acknowledgementId, { final_values: selectedValues });
      // Collapse the panel and refresh BEFORE notifying. The row must disappear
      // on its own — previously the toast ran first and, when it threw, took the
      // refresh down with it, leaving a resolved vendor on screen until a manual
      // page reload.
      setSelectedVendor(null);
      setSelectedValues({});
      await fetchConflictedVendors();
      toast.success('Final values sent to vendor for confirmation');
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to resolve conflict');
    }
  };

  const openRejectionModal = (acknowledgement) => {
    setRejectionVendor(acknowledgement);
    setShowRejectionOptions(true);
    setShowRejectionForm(false);
    setShowPermanentRejectionConfirmation(false);
    setRejectionType(null);
    setRejectionReason('');
    setCustomReason('');
    setRejectionRemarks('');
  };

  const handleSelectRejectionType = (type) => {
    setRejectionType(type);
    setShowRejectionOptions(false);
    if (type === 'permanent') {
      setShowPermanentRejectionConfirmation(true);
    } else if (type === 'rectification') {
      setShowRejectionForm(true);
    }
  };

  const handleConfirmPermanentRejection = () => {
    setShowPermanentRejectionConfirmation(false);
    setShowRejectionForm(true);
  };

  const closeRejectionModal = () => {
    setRejectionVendor(null);
    setShowRejectionOptions(false);
    setShowRejectionForm(false);
    setShowPermanentRejectionConfirmation(false);
    setRejectionType(null);
    setRejectionReason('');
    setCustomReason('');
    setRejectionRemarks('');
  };

  const executeRejection = async () => {
    if (!rejectionVendor) return;

    if ((rejectionType === 'rectification' || rejectionType === 'simple') && !rejectionRemarks.trim()) {
      toast.warning(rejectionType === 'rectification'
        ? 'Please provide additional remarks for rectification'
        : 'Please provide a reason for rejection');
      return;
    }

    if (rejectionType === 'permanent') {
      if (!rejectionReason) {
        toast.warning('Please select a rejection reason');
        return;
      }
      if (rejectionReason === 'other' && !customReason.trim()) {
        toast.warning('Please specify the custom reason');
        return;
      }
    }

    const vendorId = rejectionVendor.vendor_id;
    const companyName = rejectionVendor.vendor?.company_name || 'Vendor';

    let finalReason = '';
    if (rejectionType === 'permanent') {
      finalReason = rejectionReason === 'other'
        ? customReason
        : (REJECTION_REASONS.find(r => r.value === rejectionReason)?.label || rejectionReason);
    }
    const fullRejectionMessage = rejectionType === 'permanent'
      ? (rejectionRemarks ? `${finalReason} - ${rejectionRemarks}` : finalReason)
      : rejectionRemarks;

    setIsRejecting(true);
    try {
      if (rejectionType === 'permanent') {
        await approvalService.submitDecision(
          vendorId,
          'LEVEL_1',
          'REJECTED',
          `Rejected: ${fullRejectionMessage}`
        );
        try {
          await apiClient.post(`/vendors/${vendorId}/send-level1-rejection-notification`, {
            rejection_reason: fullRejectionMessage,
          });
        } catch (emailErr) {
          console.error('Error sending rejection notification:', emailErr);
        }
        await vendorService.permanentDeleteVendor(vendorId);
      } else if (rejectionType === 'rectification') {
        const updatedValues =
          rejectionVendor.id === selectedVendor && Object.keys(selectedValues).length > 0
            ? selectedValues
            : undefined;
        await acknowledgementService.requestCorrection(rejectionVendor.id, {
          additional_remarks: rejectionRemarks,
          ...(updatedValues ? { updated_values: updatedValues } : {}),
        });
      } else {
        await approvalService.submitDecision(
          vendorId,
          'LEVEL_1',
          'REJECTED',
          `Rejected: ${fullRejectionMessage}`
        );
        try {
          await apiClient.post(`/vendors/${vendorId}/send-level1-rejection-notification`, {
            rejection_reason: fullRejectionMessage,
          });
        } catch (emailErr) {
          console.error('Error sending rejection notification:', emailErr);
        }
      }

      // Close modal and update UI immediately before any refresh
      closeRejectionModal();
      setSelectedVendor(null);
      setSelectedValues({});

      // Defer toast so modal closes and unmounts first (toast z-index is below modal)
      const successMessage =
        rejectionType === 'permanent'
          ? 'Vendor rejected and deleted permanently'
          : rejectionType === 'rectification'
            ? 'Correction request sent. Vendor can resubmit the questionnaire.'
            : `${companyName} has been rejected.`;
      setTimeout(() => {
        if (rejectionType === 'permanent' || rejectionType === 'rectification') {
          toast.success(successMessage);
        } else {
          toast.info(successMessage);
        }
      }, 0);

      // Refresh list in background - don't let failures block the UI update
      fetchConflictedVendors().catch((refreshErr) => {
        console.error('Failed to refresh conflicted vendors list:', refreshErr);
        toast.error('List refresh failed. Please refresh the page.');
      });
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      toast.error(`Failed to reject vendor: ${errorMsg}`);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleReject = (acknowledgement) => {
    openRejectionModal(acknowledgement);
  };

  // Map field keys to their corresponding option arrays
  const fieldOptionsMap = {
    supplier_term_of_payment: supplierTermsOfPayment,
    supplier_payment_method: supplierPaymentMethods,
    supplier_delivery_terms: supplierDeliveryTerms,
    supplier_mode_of_delivery: supplierModeOfDelivery,
  };

  const fields = [
    { key: 'supplier_term_of_payment', label: 'Term of Payment' },
    { key: 'supplier_payment_method', label: 'Payment Method' },
    { key: 'supplier_delivery_terms', label: 'Delivery Terms' },
    { key: 'supplier_mode_of_delivery', label: 'Mode of Delivery' },
  ];

  const renderEditableComparison = (vendor) => {
    // Safely handle old data structure - ensure questionnaire_data and vendor_response are objects
    const adminData = (vendor.questionnaire_data && typeof vendor.questionnaire_data === 'object') 
      ? vendor.questionnaire_data 
      : {};
    const vendorData = (vendor.vendor_response && typeof vendor.vendor_response === 'object')
      ? vendor.vendor_response
      : {};

    const getAgreementStatus = (fieldKey) => {
      const adminValue = adminData[fieldKey];
      const vendorValue = vendorData[fieldKey];
      const selectedValue = selectedValues[fieldKey];
      const isAgreed = selectedValue === vendorValue;
      const hasChange = adminValue !== vendorValue;
      
      return { adminValue, vendorValue, selectedValue, isAgreed, hasChange };
    };

    return (
      <div className="space-y-4">
        {fields.map((field) => {
          const status = getAgreementStatus(field.key);
          
          return (
            <div
              key={field.key}
              className={`border rounded-lg p-4 ${status.hasChange ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'}`}
            >
              <h4 className="font-medium text-gray-900 mb-3">{field.label}</h4>
              
              {/* Admin's original value - read-only reference */}
              <div className="bg-blue-50 p-2 rounded mb-2">
                <p className="text-xs text-gray-500 mb-1">Admin's Original Value:</p>
                <p className="text-sm text-blue-700 font-medium">
                  {getQuestionnaireLabel(field.key, status.adminValue) || 'Not specified'}
                </p>
              </div>
              
              {/* Vendor's proposed value - read-only reference */}
              <div className="bg-amber-50 p-2 rounded mb-2">
                <p className="text-xs text-gray-500 mb-1">Vendor's Proposed Value:</p>
                <p className="text-sm text-amber-700 font-medium">
                  {getQuestionnaireLabel(field.key, status.vendorValue) || 'Not specified'}
                </p>
              </div>
              
              {/* Admin's final selection - EDITABLE dropdown */}
              <div className="mt-3">
                <Select
                  label="Final Approved Value"
                  options={fieldOptionsMap[field.key] || []}
                  value={selectedValues[field.key] || ''}
                  onChange={(value) => setSelectedValues(prev => ({
                    ...prev,
                    [field.key]: value
                  }))}
                  placeholder={`Select ${field.label}`}
                  searchable
                  clearable
                  required
                />
                
                {/* Agreement indicator */}
                {selectedValues[field.key] && (
                  <div className="mt-2">
                    {status.isAgreed ? (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Agreement with vendor
                      </span>
                    ) : (
                      <span className="text-xs text-amber-600 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Admin override
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center mb-2">
          <AlertTriangle className="w-8 h-8 text-amber-500 mr-3" />
          <h1 className="text-3xl font-bold text-gray-900">Conflicting Vendors</h1>
        </div>
        <p className="text-gray-600">
          Review and resolve vendor-proposed changes to Level 1 questionnaires
        </p>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by company name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Vendor List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : vendors.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <p className="text-gray-600">No conflicting vendors at this time</p>
        </div>
      ) : (
        <div className="space-y-4">
          {vendors.map((vendor) => (
            <div key={vendor.id} className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{vendor.vendor.company_name}</h3>
                  <p className="text-sm text-gray-600">{vendor.vendor.email}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Submitted: {new Date(vendor.used_at).toLocaleDateString()}
                  </p>
                </div>
                <span className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                  Conflicted
                </span>
              </div>

              {selectedVendor === vendor.id ? (
                <>
                  {renderEditableComparison(vendor)}
                  <div className="flex gap-4 mt-6">
                    <button
                      onClick={() => {
                        setSelectedVendor(null);
                        setSelectedValues({});
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleReject(vendor)}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                      Reject Vendor
                    </button>
                    <button
                      onClick={() => handleResolve(vendor.id)}
                      disabled={!Object.keys(selectedValues).length || 
                        !selectedValues.supplier_term_of_payment ||
                        !selectedValues.supplier_payment_method ||
                        !selectedValues.supplier_delivery_terms ||
                        !selectedValues.supplier_mode_of_delivery}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={
                        (!selectedValues.supplier_term_of_payment ||
                         !selectedValues.supplier_payment_method ||
                         !selectedValues.supplier_delivery_terms ||
                         !selectedValues.supplier_mode_of_delivery)
                          ? 'Please select values for all fields'
                          : 'Approve selected values'
                      }
                    >
                      Approve Changes
                    </button>
                  </div>
                </>
              ) : (
                <button
                  onClick={() => setSelectedVendor(vendor.id)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Review Changes
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {meta.totalPages > 1 && (
        <div className="mt-6 flex justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2">
            Page {page} of {meta.totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
            disabled={page === meta.totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Rejection Options Modal */}
      {showRejectionOptions && rejectionVendor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[101] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-md relative">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">Select Rejection Action</h3>
              <p className="text-sm text-slate-600 mt-1">Choose how you want to reject this vendor</p>
            </div>
            <div className="p-6 space-y-4">
              <Button
                variant="destructive"
                onClick={() => handleSelectRejectionType('permanent')}
                className="w-full"
                iconName="XCircle"
                iconPosition="left"
              >
                Reject Vendor (Permanent)
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSelectRejectionType('rectification')}
                className="w-full"
                iconName="Edit"
                iconPosition="left"
              >
                Reject with Correction
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setRejectionType('simple');
                  setShowRejectionOptions(false);
                  setShowRejectionForm(true);
                }}
                className="w-full"
              >
                Reject (Simple)
              </Button>
            </div>
            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button variant="outline" onClick={closeRejectionModal}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Rejection Confirmation */}
      {showPermanentRejectionConfirmation && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[102] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border-2 border-red-500 shadow-xl w-full max-w-md relative">
            <div className="p-6 border-b border-red-200 bg-red-50">
              <h3 className="text-xl font-bold text-red-900">Warning</h3>
            </div>
            <div className="p-6">
              <p className="text-lg font-semibold text-red-900 text-center mb-4">
                You have chosen to reject the vendor. All details of this vendor will be deleted permanently. Wish to proceed?
              </p>
            </div>
            <div className="p-6 border-t border-red-200 flex items-center justify-end gap-3">
              <Button variant="outline" onClick={closeRejectionModal}>
                No / Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirmPermanentRejection}>
                Yes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rejection Form Modal */}
      {showRejectionForm && rejectionVendor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[101] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-md relative">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">
                {rejectionType === 'rectification' ? 'Reject with Correction' : 'Reject Application'}
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                {rejectionType === 'rectification'
                  ? 'Please provide remarks about what needs to be corrected'
                  : 'Please provide a reason for rejection'}
              </p>
            </div>
            <div className="p-6 space-y-4">
              {rejectionType === 'permanent' && (
                <>
                  <Select
                    label="Rejection Reason *"
                    placeholder="Select reason for rejection"
                    options={REJECTION_REASONS}
                    value={rejectionReason}
                    onChange={setRejectionReason}
                  />
                  {rejectionReason === 'other' && (
                    <Input
                      label="Custom Reason *"
                      type="text"
                      placeholder="Specify the reason..."
                      value={customReason}
                      onChange={(e) => setCustomReason(e.target.value)}
                    />
                  )}
                </>
              )}
              {rejectionType === 'rectification' &&
                rejectionVendor?.id === selectedVendor &&
                Object.values(selectedValues).some(Boolean) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
                    <p className="text-xs font-semibold text-blue-800 mb-1">
                      Counter-proposal values that will be sent to vendor:
                    </p>
                    {fields.map((f) =>
                      selectedValues[f.key] ? (
                        <div key={f.key} className="flex justify-between text-xs">
                          <span className="text-gray-600">{f.label}:</span>
                          <span className="text-blue-800 font-medium">
                            {getQuestionnaireLabel(f.key, selectedValues[f.key])}
                          </span>
                        </div>
                      ) : null
                    )}
                  </div>
                )}
              <Input
                label={rejectionType === 'rectification' ? 'Additional Remarks *' : 'Additional Remarks'}
                type="textarea"
                placeholder={rejectionType === 'rectification' ? 'Specify what needs to be corrected...' : 'Any additional comments...'}
                value={rejectionRemarks}
                onChange={(e) => setRejectionRemarks(e.target.value)}
              />
            </div>
            <div className="p-6 border-t border-slate-200 flex items-center justify-end gap-3">
              <Button variant="outline" onClick={closeRejectionModal}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={executeRejection}
                disabled={isRejecting}
                loading={isRejecting}
              >
                Confirm Rejection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
      />
    </div>
  );
};

export default ConflictingVendors;
